<template>
    <div>
        <!-- Full-screen loading overlay -->
        <v-overlay v-model="pageLoading" class="align-center justify-center" persistent>
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="64"
                    color="primary"
                ></v-progress-circular>
                <div class="mt-4 text-h6">Đang tải dữ liệu...</div>
            </div>
        </v-overlay>

        <v-btn color="primary" class="mb-4" @click="showAddForm = !showAddForm">
            <v-icon left>mdi-plus-box</v-icon>
            {{ showAddForm ? 'Đóng' : 'Thêm sản phẩm mới' }}
        </v-btn>
        <v-card class="mb-6" v-if="showAddForm">
            <!-- Form tạo mới sản phẩm -->
            <v-card-title class="text-h6">
                <v-icon class="mr-2">mdi-plus-box</v-icon>
                Thêm sản phẩm mới
            </v-card-title>
            <v-card-text>
                <v-form @submit.prevent="addProduct" ref="form" v-model="valid">
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="form.name" label="Tên sản phẩm" :rules="[v => !!v || 'Bắt buộc']" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.price" label="Giá (VNĐ)" type="number" :rules="[v => v >= 0 || 'Không âm']" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.sale_off" label="Giảm giá (VNĐ)" type="number" :rules="[v => v >= 0 || 'Không âm']" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.number="form.affiliate_share" label="Affiliate Share (%)" type="number" :rules="[v => v >= 0 || 'Không âm']" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select v-model="form.category_id" :items="categories" item-title="name" item-value="id" label="Danh mục" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="form.tags"
                                :items="availableTags"
                                label="Tags"
                                placeholder="Tìm kiếm và chọn tags hoặc nhập tag mới..."
                                variant="outlined"
                                density="compact"
                                multiple
                                chips
                                closable-chips
                                clearable
                                :search-input.sync="tagSearch"
                                @update:search="onTagSearch"
                                @update:model-value="onTagsChange"
                                @keydown.enter="addNewTag(tagSearch)"
                                :loading="loadingTags"
                                :filter="() => true"
                                item-title="name"
                                item-value="name"
                                hint="Chọn tags từ danh sách có sẵn hoặc nhập tag mới và nhấn Enter"
                                persistent-hint
                                allow-input
                            >
                                <template v-slot:chip="{ props, item }">
                                    <v-chip
                                        v-bind="props"
                                        :text="item.raw.name"
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                </template>
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props">
                                        <template v-slot:prepend>
                                            <v-icon icon="mdi-tag" color="primary" size="small" />
                                        </template>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6" class="d-flex align-center">
                            <v-checkbox
                                v-model="form.is_license"
                                label="Sản phẩm có License (Key)"
                                color="primary"
                                hide-details
                                :true-value="1"
                                :false-value="0"
                            ></v-checkbox>
                        </v-col>
                        <v-col cols="12" md="6">
                            <label class="mb-2 font-weight-medium">Thumbnail</label>
                            <file-input
                                v-model="form.thumbnail"
                                accept="image/*"
                                title="Tải ảnh thumbnail"
                                subtitle="Chọn ảnh đại diện cho sản phẩm"
                                :max-size="5 * 1024 * 1024"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <label class="mb-2 font-weight-medium">Mô tả sản phẩm</label>
                            <html-editor
                                v-model="form.description"
                                height="300px"
                                placeholder="Nhập mô tả sản phẩm (HTML)..."
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <label class="mb-2 font-weight-medium">Template Email</label>
                            <html-editor
                                v-model="form.template_email"
                                height="300px"
                                placeholder="Nhập template email (HTML)..."
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.library_link"
                                label="Link thư viện Apps Script"
                                placeholder="https://script.google.com/d/..."
                                variant="outlined"
                                density="compact"
                                clearable
                                hint="Để trống nếu sản phẩm không có thư viện. Khách mua xong sẽ được tự cấp quyền xem."
                                persistent-hint
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.license_sheet_link"
                                label="Link danh sách bản quyền (Google Sheet)"
                                placeholder="https://docs.google.com/spreadsheets/d/..."
                                variant="outlined"
                                density="compact"
                                clearable
                                hint="Để trống nếu không dùng. Khách mua xong sẽ được thêm email vào một dòng mới trong sheet này."
                                persistent-hint
                            />
                        </v-col>
                        <v-col cols="12">
                            <label class="mb-2 font-weight-medium">Landing Page (File ZIP)</label>
                            <file-input
                                v-model="form.landing_page_file"
                                accept=".zip"
                                title="Tải file ZIP Landing Page"
                                subtitle="Chọn file ZIP chứa trang landing page"
                                :max-size="50 * 1024 * 1024"
                            />
                        </v-col>
                        <v-col cols="12">
                            <label class="mb-2 font-weight-medium">Slide Ảnh</label>
                            <div class="slide-images-container">
                                <div v-for="(img, idx) in form.slide_img" :key="idx" class="slide-image-item">
                                    <file-input
                                        v-model="form.slide_img[idx]"
                                        accept="image/*"
                                        :title="`Slide ${idx + 1}`"
                                        subtitle="Chọn ảnh cho slide"
                                        :max-size="5 * 1024 * 1024"
                                    />
                                    <v-btn 
                                        icon 
                                        color="error" 
                                        @click="removeSlideImg(idx)" 
                                        v-if="form.slide_img.length > 1"
                                        class="remove-slide-btn"
                                    >
                                        <v-icon>mdi-minus</v-icon>
                                    </v-btn>
                                </div>
                                <div class="add-slide-container">
                                    <v-btn color="primary" @click="addSlideImg" outlined>
                                        <v-icon left>mdi-plus</v-icon>
                                        Thêm slide
                                    </v-btn>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="12" class="d-flex justify-center">
                            <v-btn color="primary" type="submit" :loading="loading" :disabled="!valid">
                                <v-icon class="mr-2">mdi-content-save</v-icon>
                                Lưu sản phẩm
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- Bảng danh sách sản phẩm -->
        <v-card>
            <v-card-title class="text-h6 d-flex align-center justify-space-between">
                <div class="d-flex align-center">
                    <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                    Danh sách sản phẩm
                </div>
                <v-text-field
                    v-model="search"
                    label="Tìm kiếm sản phẩm"
                    @input="onSearchInput"
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    density="compact"
                    class="search-input"
                    style="max-width: 320px;"
                />
            </v-card-title>
            <v-card-text>
                <!-- Desktop Table View -->
                <div v-if="!$vuetify.display.mobile" class="desktop-view">
                    <v-table class="elevation-1">
                        <thead>
                            <tr>
                                <th>Tên sản phẩm</th>
                                <th>Giá</th>
                                <th>Giảm giá</th>
                                <th>Affiliate Share</th>
                                <th>Danh mục</th>
                                <th>Tags</th>
                                <th class="text-center">Trạng thái</th>
                                <th class="text-center">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="product in allProducts" :key="product.id">
                                <td>{{ product.name }}</td>
                                <td>{{ product.price.toLocaleString() }}₫</td>
                                <td>{{ product.sale_off.toLocaleString() }}₫</td>
                                <td>{{ product.affiliate_share ? product.affiliate_share + '%' : 'Không' }}</td>
                                <td>{{ getCategoryName(product.category_id) }}</td>
                                <td>
                                    <div v-if="product.tags" class="d-flex flex-wrap">
                                        <v-chip 
                                            v-for="tag in product.tags.split(',')" 
                                            :key="tag.trim()"
                                            size="x-small" 
                                            color="primary" 
                                            variant="outlined"
                                            class="ma-1">
                                            {{ tag.trim() }}
                                        </v-chip>
                                    </div>
                                    <span v-else>-</span>
                                </td>
                                <td class="text-center">
                                    <v-chip 
                                        :color="product.status ? 'success' : 'error'" 
                                        size="small" 
                                        variant="outlined">
                                        <v-icon start>{{ product.status ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                                        {{ product.status ? 'Hiện' : 'Ẩn' }}
                                    </v-chip>
                                </td>
                                <td class="text-center">
                                    <v-btn icon size="small" color="primary" @click="editProduct(product)"><v-icon>mdi-pencil</v-icon></v-btn>
                                    <v-btn icon size="small" :color="product.status ? 'success' : 'error'" @click="toggleProduct(product)">
                                        <v-icon>{{ product.status ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                    <v-pagination
                        v-model="page"
                        :length="totalPages"
                        @update:modelValue="onPageChange"
                        class="custom-pagination"
                    />
                </div>

                <!-- Mobile Card View -->
                <div v-else class="mobile-view">
                    <div class="mobile-products-container" ref="productsContainer">
                        <v-card 
                            v-for="product in allProducts" 
                            :key="product.id" 
                            class="mb-3 product-card"
                            elevation="2">
                            <v-card-text>
                                <div class="d-flex justify-space-between align-center mb-2">
                                    <div class="product-name font-weight-bold">{{ product.name }}</div>
                                    <v-chip :color="product.status ? 'error' : 'success'" size="small">
                                        {{ product.status ? 'Ẩn' : 'Hiển thị' }}
                                    </v-chip>
                                </div>
                                
                                <div class="product-info">
                                    <div class="info-row">
                                        <v-icon size="16" color="success" class="mr-2">mdi-currency-usd</v-icon>
                                        <strong>Giá:</strong>
                                        <span class="ml-2 text-success font-weight-bold">{{ product.price.toLocaleString() }}₫</span>
                                    </div>
                                    
                                    <div class="info-row" v-if="product.sale_off > 0">
                                        <v-icon size="16" color="error" class="mr-2">mdi-tag</v-icon>
                                        <strong>Giảm giá:</strong>
                                        <span class="ml-2 text-error">{{ product.sale_off.toLocaleString() }}₫</span>
                                    </div>
                                    
                                    <div class="info-row" v-if="product.affiliate_share">
                                        <v-icon size="16" color="orange" class="mr-2">mdi-percent</v-icon>
                                        <strong>Affiliate:</strong>
                                        <span class="ml-2">{{ product.affiliate_share }}%</span>
                                    </div>
                                    
                                    <div class="info-row">
                                        <v-icon size="16" color="info" class="mr-2">mdi-folder</v-icon>
                                        <strong>Danh mục:</strong>
                                        <span class="ml-2">{{ getCategoryName(product.category_id) }}</span>
                                    </div>
                                    
                                    <div class="info-row" v-if="product.tags">
                                        <v-icon size="16" color="purple" class="mr-2">mdi-tag-multiple</v-icon>
                                        <strong>Tags:</strong>
                                        <div class="ml-2 d-flex flex-wrap">
                                            <v-chip 
                                                v-for="tag in product.tags.split(',')" 
                                                :key="tag.trim()"
                                                size="x-small" 
                                                color="primary" 
                                                variant="outlined"
                                                class="ma-1">
                                                {{ tag.trim() }}
                                            </v-chip>
                                        </div>
                                    </div>
                                    
                                    <div class="info-row">
                                        <v-icon size="16" :color="product.status ? 'success' : 'error'" class="mr-2">{{ product.status ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                                        <strong>Trạng thái:</strong>
                                        <v-chip 
                                            :color="product.status ? 'success' : 'error'" 
                                            size="small" 
                                            variant="outlined"
                                            class="ml-2">
                                            {{ product.status ? 'Hiện' : 'Ẩn' }}
                                        </v-chip>
                                    </div>
                                </div>
                                
                                <div class="d-flex justify-center mt-3 gap-2">
                                    <v-btn color="primary" size="small" @click="editProduct(product)">
                                        <v-icon class="mr-1">mdi-pencil</v-icon>
                                        Sửa
                                    </v-btn>
                                    <v-btn :color="product.status ? 'success' : 'error'" size="small" @click="toggleProduct(product)">
                                        <v-icon class="mr-1">{{ product.status ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                                        {{ product.status ? 'Hiện' : 'Ẩn' }}
                                    </v-btn>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                    
                    <!-- Loading indicator for infinite scroll -->
                    <div v-if="loadingMore" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                        <div class="mt-2">Đang tải thêm...</div>
                    </div>
                    
                    <!-- End of list indicator -->
                    <div v-if="hasReachedEnd && allProducts.length > 0" class="text-center py-4 text-grey">
                        <v-icon>mdi-check-circle</v-icon>
                        <div>Đã hiển thị tất cả sản phẩm</div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top right>
            {{ snackbarText }}
        </v-snackbar>
    </div>
</template>

<script>
import HtmlEditor from '@/components/HtmlEditor.vue';
import FileInput from '@/components/FileInput.vue';
import axios from '@/http';
export default {
    name: 'Product',
    components: { HtmlEditor, FileInput },
    data() {
        return {
            valid: false,
            loading: false,
            pageLoading: false, // Full-screen loading state
            categories: [],
            form: {
                name: '',
                description: '',
                thumbnail: '',
                slide_img: [''],
                price: 0,
                sale_off: 0,
                template_email: '',
                affiliate_share: 0,
                category_id: null,
                landing_page_file: null,
                library_link: '',
                license_sheet_link: '',
                tags: [],
                status: 1,
                is_license: 0,
            },
            products: [],
            allProducts: [], // For infinite scroll
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
            page: 1,
            limit: 10,
            search: '',
            total: 0,
            totalPages: 1,
            showAddForm: false,
            loadingMore: false, // For infinite scroll loading
            hasReachedEnd: false, // Track if we've loaded all data
            currentMobilePage: 1, // Separate page tracking for mobile
            availableTags: [], // Available tags for autocomplete
            loadingTags: false, // Loading state for tags
            tagSearch: '', // Search input for tags
        }
    },
    methods: {
        addSlideImg() {
            this.form.slide_img.push('');
        },
        removeSlideImg(idx) {
            if (this.form.slide_img.length > 1) this.form.slide_img.splice(idx, 1);
        },
        getCategoryName(id) {
            const cat = this.categories.find(c => c.id === id);
            return cat ? cat.name : '';
        },
        async addProduct() {
            if (!this.$refs.form.validate()) return;
            this.loading = true;
            
            // Tạo payload và xử lý dữ liệu từ FileInput components
            const payload = { ...this.form };
            
            // Xử lý slide images (chỉ lấy những slide có data)
            payload.slide = this.form.slide_img.filter(img => img && img.trim() !== '');
            delete payload.slide_img;
            
            // Xử lý tags (chuyển array thành string nếu cần)
            if (Array.isArray(this.form.tags)) {
                payload.tags = this.form.tags.join(',');
            }
            
            // Debug form data
            console.log('🔍 Form data check:', {
                description: this.form.description,
                template_email: this.form.template_email,
                landing_page_file: this.form.landing_page_file
            });
            
            // Xử lý landing page file
            console.log('🔍 Landing page file check:', this.form.landing_page_file);
            if (this.form.landing_page_file && this.form.landing_page_file.data) {
                console.log('✅ Landing page file data found, adding to payload');
                payload.landing_page = this.form.landing_page_file.data;
            } else {
                console.log('❌ Landing page file data NOT found');
            }
            delete payload.landing_page_file;
            
            try {
                await axios.post('/products', payload);
                this.products.unshift({
                    id: Date.now(),
                    ...this.form,
                    status: 1
                });
                
                // Reset form
                // this.form = {
                //     name: '',
                //     description: '',
                //     thumbnail: '',
                //     slide_img: [''],
                //     price: 0,
                //     sale_off: 0,
                //     template_email: '',
                //     affiliate_share: 0,
                //     category_id: null,
                //     landing_page_file: null
                // };
                
                this.snackbarText = 'Tạo sản phẩm thành công!';
                this.snackbarColor = 'success';
                this.snackbar = true;
            } catch (e) {
                console.error('Error creating product:', e);
                this.snackbarText = 'Tạo sản phẩm thất bại!';
                this.snackbarColor = 'error';
                this.snackbar = true;
            }
            this.loading = false;
        },
        editProduct(product) {
            this.$router.push(`/admin/update-product/${product.id}`);
        },
        async toggleProduct(product) {
            const newStatus = product.status ? 0 : 1;
            try {
                await axios.put(`/products/${product.id}/status`, { status: newStatus });
                product.status = newStatus;
                this.snackbarText = 'Cập nhật trạng thái sản phẩm thành công!';
                this.snackbarColor = 'success';
                this.snackbar = true;
            } catch (e) {
                this.snackbarText = 'Cập nhật trạng thái sản phẩm thất bại!';
                this.snackbarColor = 'error';
                this.snackbar = true;
            }
        },
        async fetchProducts(page = this.page, limit = this.limit, search = this.search, showPageLoading = false, append = false) {
            if (showPageLoading) {
                this.pageLoading = true;
            }
            if (append) {
                this.loadingMore = true;
            }
            
            try {
                const res = await axios.get('/products', {
                    params: { page, limit, search }
                });
                
                if (append) {
                    // For mobile infinite scroll
                    this.allProducts = [...this.allProducts, ...res.data.data];
                    this.hasReachedEnd = res.data.data.length < limit || page >= res.data.totalPages;
                } else {
                    // For desktop pagination or initial load
                    this.products = res.data.data;
                    this.allProducts = res.data.data;
                }
                
                this.total = res.data.total;
                this.page = res.data.page;
                this.totalPages = res.data.totalPages;
            } catch (e) {
                this.snackbarText = 'Lấy danh sách sản phẩm thất bại!';
                this.snackbarColor = 'error';
                this.snackbar = true;
            } finally {
                if (showPageLoading) {
                    this.pageLoading = false;
                }
                if (append) {
                    this.loadingMore = false;
                }
            }
        },

        async loadMoreProducts() {
            if (this.loadingMore || this.hasReachedEnd) return;
            
            this.currentMobilePage++;
            await this.fetchProducts(
                this.currentMobilePage, 
                this.limit, 
                this.search, 
                false, 
                true // append = true
            );
        },

        resetMobileData() {
            this.allProducts = [];
            this.currentMobilePage = 1;
            this.hasReachedEnd = false;
        },
        async fetchCategories(showPageLoading = false) {
            try {
                const response = await axios.get('/categories');
                this.categories = response.data;
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        },

        async searchTags(search = '', page = 1, limit = 20) {
            this.loadingTags = true;
            try {
                const response = await axios.get('/products/tags/simple', {
                    params: { search, page, limit }
                });
                
                if (response.data && response.data.success) {
                    // Response có dạng: { success: true, data: ["tag1", "tag2"], total: 2, search: "..." }
                    const tags = response.data.data || [];
                    // Chuyển đổi từ array string thành array object để phù hợp với v-autocomplete
                    this.availableTags = tags.map(tag => ({
                        name: tag,
                        count: 0 // Không có count trong response này
                    }));
                }
            } catch (error) {
                console.error('Error searching tags:', error);
                this.availableTags = [];
            } finally {
                this.loadingTags = false;
            }
        },

        onTagSearch(search) {
            this.tagSearch = search;
            if (search && search.length >= 2) {
                // Debounce search
                clearTimeout(this.tagSearchTimeout);
                this.tagSearchTimeout = setTimeout(() => {
                    this.searchTags(search);
                }, 300);
            } else if (!search) {
                // Load initial tags when search is cleared
                this.searchTags();
            }
        },

        addNewTag(tagName) {
            if (tagName && tagName.trim()) {
                const trimmedTag = tagName.trim();
                // Kiểm tra xem tag đã tồn tại chưa
                const existingTag = this.availableTags.find(t => t.name === trimmedTag);
                if (!existingTag) {
                    // Thêm tag mới vào availableTags
                    this.availableTags.push({
                        name: trimmedTag,
                        count: 0
                    });
                }
                // Thêm tag vào form.tags nếu chưa có
                if (!this.form.tags.includes(trimmedTag)) {
                    this.form.tags.push(trimmedTag);
                }
            }
        },

        onTagsChange(newTags) {
            // Xử lý khi tags thay đổi
            if (Array.isArray(newTags)) {
                // Kiểm tra xem có tag mới nào không
                newTags.forEach(tag => {
                    if (typeof tag === 'string' && tag.trim()) {
                        // Nếu là tag mới (không có trong availableTags)
                        const existingTag = this.availableTags.find(t => t.name === tag);
                        if (!existingTag) {
                            // Thêm tag mới vào availableTags
                            this.availableTags.push({
                                name: tag.trim(),
                                count: 0
                            });
                        }
                    }
                });
            }
        },
        onSearchInput() {
            clearTimeout(this._searchTimeout);
            this._searchTimeout = setTimeout(() => {
                this.page = 1;
                this.resetMobileData();
                this.fetchProducts(1, this.limit, this.search);
            }, 400); // debounce 400ms
        },
        onPageChange(newPage) {
            this.page = newPage;
            this.fetchProducts(newPage, this.limit, this.search);
        },

        setupInfiniteScroll() {
            if (!('IntersectionObserver' in window)) return;
            
            this.$nextTick(() => {
                if (this.$refs.productsContainer) {
                    this.observer = new IntersectionObserver((entries) => {
                        const target = entries[0];
                        if (target.isIntersecting && this.$vuetify.display.mobile) {
                            this.loadMoreProducts();
                        }
                    }, {
                        root: null,
                        rootMargin: '200px',
                        threshold: 0.1
                    });
                    
                    // Create a sentinel element at the bottom
                    const sentinel = document.createElement('div');
                    sentinel.style.height = '1px';
                    sentinel.style.background = 'transparent';
                    this.$refs.productsContainer?.appendChild(sentinel);
                    this.observer.observe(sentinel);
                }
            });
        }
    },
    mounted() {
        this.fetchCategories();
        this.fetchProducts(this.page, this.limit, this.search, true); // Show page loading on initial load
        this.setupInfiniteScroll();
        this.searchTags(); // Load tags ban đầu
    },
    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    watch: {
        'form.landing_page_file': {
            handler(newVal, oldVal) {
                console.log('🔄 Landing page file changed:', {
                    old: oldVal,
                    new: newVal
                });
            },
            deep: true
        },
        'form.description': {
            handler(newVal, oldVal) {
                console.log('🔄 Description changed:', {
                    old: oldVal,
                    new: newVal
                });
            }
        },
        'form.template_email': {
            handler(newVal, oldVal) {
                console.log('🔄 Template email changed:', {
                    old: oldVal,
                    new: newVal
                });
            }
        }
    },
    async mounted() {
        this.pageLoading = true;
        try {
            await Promise.all([
                this.fetchProducts(this.page, this.limit, this.search),
                this.fetchCategories()
            ]);
        } finally {
            this.pageLoading = false;
        }
    }
}
</script>

<style scoped>
.font-weight-medium {
    font-weight: 500;
}

.search-input {
    max-width: 320px;
    margin-bottom: 0;
}

.slide-images-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.slide-image-item {
    position: relative;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px;
    background-color: #fafafa;
}

.remove-slide-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 10;
}

.add-slide-container {
    display: flex;
    justify-content: center;
    padding: 16px;
    border: 2px dashed #ddd;
    border-radius: 8px;
    background-color: #fafafa;
}

.add-slide-container:hover {
    border-color: #1976d2;
    background-color: #f3f8ff;
}

/* Mobile Card Styles */
.mobile-view {
  padding: 0;
}

.mobile-products-container {
  padding: 8px 0;
}

.product-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-height: 24px;
  font-size: 14px;
}

.info-row strong {
  min-width: 70px;
  font-weight: 600;
}

.info-row .ml-2 {
  flex: 1;
  word-break: break-word;
}

.product-name {
  word-break: break-word;
  line-height: 1.3;
}

/* Desktop responsive adjustments */
@media (max-width: 1200px) {
  .desktop-view .v-table thead th {
    font-size: 13px;
    padding: 8px 4px;
  }
  
  .desktop-view .v-table tbody td {
    font-size: 13px;
    padding: 8px 4px;
  }
}

@media (max-width: 768px) {
    .slide-images-container {
        gap: 12px;
    }
    
    .slide-image-item {
        padding: 8px;
    }
    
    .search-input {
        max-width: 100% !important;
        margin-bottom: 16px;
    }
    
    .v-card-title {
        flex-direction: column !important;
        align-items: stretch !important;
        gap: 16px;
    }
    
    .mobile-products-container {
        padding: 4px 0;
    }
    
    .product-card {
        margin-bottom: 12px !important;
    }
    
    .info-row {
        font-size: 13px;
    }
    
    .info-row strong {
        min-width: 65px;
        font-size: 13px;
    }
    
    .product-name {
        font-size: 15px;
    }
}
</style>